import { supabase } from './client';

export interface AppNotification {
  id: string;
  user_id: string;
  type: 'comment_on_note' | 'new_follower' | 'outbid_on_product' | 'product_sold' | 'product_won' | 'order_update' | 'assignment_request' | 'assignment_declined' | 'assignment_accepted' | 'store_member_added' | 'auction_won' | 'auction_expired';
  title?: string;
  message: string;
  link: string | null;
  reference_id?: string | null;
  reference_type?: string | null;
  is_read: boolean;
  created_at: string;
}

export const createNotification = async (
  userId: string,
  type: AppNotification['type'],
  message: string,
  link: string | null = null
) => {
  // Prefer calling the server-side RPC which runs as SECURITY DEFINER
  try {
    const { data, error } = await supabase.rpc('create_notification', {
      p_user_id: userId,
      p_type: type,
      p_message: message,
      p_link: link,
    });
    if (error) {
      // Fall back to direct insert if RPC not available or fails
      console.warn('create_notification RPC failed, falling back to direct insert:', error);
      const { data: d2, error: e2 } = await supabase
        .from('notifications')
        .insert({ user_id: userId, type, message, link })
        .select()
        .single();
      if (e2) throw e2;
      return d2;
    }
    return Array.isArray(data) ? data[0] : data;
  } catch (e) {
    console.error('Error creating notification via RPC/insert:', e);
    throw e;
  }
};

export const getNotificationsByUserId = async (userId: string): Promise<AppNotification[]> => {
  // Limit to 100 most recent notifications to reduce egress
  // Loading all notifications for active users can consume 50-200MB/month
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) {
    console.error('Error fetching notifications by user ID:', error);
    throw error;
  }
  // Normalize is_read to a boolean since some transports can send strings like 't'/'f' or 'true'/'false'
  const normalized = (data || []).map((row: any) => ({
    ...row,
    is_read: row.is_read === true || row.is_read === 't' || row.is_read === 'true' || row.is_read === 1 || row.is_read === '1',
  }));
  return normalized;
};

export const markNotificationAsRead = async (notificationId: string, userId: string) => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const markNotificationAsUnread = async (notificationId: string, userId: string) => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: false })
    .eq('id', notificationId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error marking notification as unread:', error);
    throw error;
  }
};

export const clearUserNotifications = async (userId: string) => {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('user_id', userId);

  if (error) {
    console.error('Error clearing user notifications:', error);
    throw error;
  }
};